/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */


import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {forkJoin, Observable} from 'rxjs';
import {finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {DATE_FORMAT, ProfileMode} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {createClaimTypeTree} from '../../shared/claim-form/claim-form-utils';
import {ClaimFormComponent} from '../../shared/claim-form/claim-form.component';
import {Contract} from '../../shared/employees/employees.model';
import {VariableZonesComponent} from '../../shared/employees/variable-zone/variable-zones.component';
import {ModalComponent, ModalService} from '../../shared/modal';
import {DefaultPagination, Pagination, TreeNode} from '../../shared/ui';
import {isMobileDevice} from '../../shared/utils/browser';
import {ClaimData, ESSService} from '../employee-self.service';

import {ClaimType, OngoingPayPeriod} from './claims.interfaces';

@Component({
  selector: 'ess-claims',
  templateUrl: './claims.component.html',
  styleUrls: ['./claims.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ClaimsComponent extends Destroyable implements OnInit {

  public static ACCESS_NAME = 'ESSClaims';

  public contractId: number;
  public cliRegId: number;

  public loading = false;
  public pagination: Pagination = new DefaultPagination();
  public claimForm: UntypedFormGroup;
  public claims: ClaimData[];
  public claimTypes: ClaimType[];
  public claimTypeTree: Array<TreeNode<any>>;
  public onGoingPayPeriod: OngoingPayPeriod;
  public readOnly: boolean;
  public isModalOpen = false;

  public submitting = false;
  public isMobile = false;

  @ViewChild(VariableZonesComponent)
  public variableZones: VariableZonesComponent;

  @ViewChild(ClaimFormComponent)
  public claimFormComponent: ClaimFormComponent;

  public selectedClaim: ClaimData;

  constructor(
    private modalService: ModalService,
    private essService: ESSService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService
  ) {
    super();
    this.initClaimForm();
  }

  public ngOnInit() {
    this.isMobile = isMobileDevice();

    this.essService.currentContract
      .pipe(
        tap((contract: Contract) => {
          this.contractId = contract.id;
          this.cliRegId = contract.cliRegID;
        }),
        switchMap(() => this.getClaimsData()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.pagination.pageChanged
      .asObservable()
      .pipe(
        switchMap(() => this.getClaimsData()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public newClaim() {
    this.isModalOpen = true;
    this.readOnly = false;
    this.modalService.openModalWithId('create-request');
  }

  public onSubmit() {
    this.submitting = true;
    const additionalZonesData = this.getAdditionalZonesData();

    this.claimForm.patchValue({
      beginDate: this.onGoingPayPeriod.beginDate,
      endDate: this.onGoingPayPeriod.endDate,
      payPeriodTransId: this.onGoingPayPeriod.transId,
      cliEmpId: this.essService.employee?.id,
      firstName: this.essService.employee?.firstName,
      lastName: this.essService.employee?.lastName,
      contractId: this.contractId,
      values: additionalZonesData ? additionalZonesData.values : null
    });
    this.createNewClaim(this.claimForm.value);
  }

  public onClose(modal?: ModalComponent) {
    this.claimForm.reset();
    this.selectedClaim = null;
    this.isModalOpen = false;
    if (modal) {
      modal.close();
    }
  }

  public getWorkflowStatus(claim: ClaimData): string {
    if (!claim.workflowStatus) {
      return 'PENDING';
    }
    return claim.workflowStatus;
  }

  public viewClaim(claim: ClaimData) {
    this.isModalOpen = true;
    this.selectedClaim = claim;
    this.readOnly = true;
    this.modalService.openModalWithId('create-request');
  }

  public viewClaimOnMobile(claim: ClaimData) {
    if (this.isMobile) {
      this.viewClaim(claim);
    }
  }

  public getUnitTransId(claim: ClaimData): string {
    if (claim.unitTransId === 'Amount' && claim.currencyString) {
      return claim.currencyString;
    } else {
      return this.translate.instant(claim.unitTransId);
    }
  }

  private initClaimForm() {
    this.claimForm = this.formBuilder.group({
      name: ['', Validators.required],
      requestLimit: ['', ''],
      value: ['', Validators.required],
      transId: ['', ''],
      unitTransId: ['', ''],
      comment: ['', ''],
      beginDate: ['', ''],
      endDate: ['', ''],
      evType: ['', ''],
      values: ['', ''],
      cliEmpId: ['', ''],
      firstName: ['', ''],
      lastName: ['', ''],
      contractId: ['', ''],
      payPeriodTransId: ['', '']
    });
  }

  private getClaimsData(): Observable<[{ total: number, data: ClaimData[] }, ClaimType[], OngoingPayPeriod]> {
    this.loading = true;
    this.claims = [];
    this.claimTypes = [];

    return forkJoin(
      [this.essService.getClaims(this.pagination.currentPage.number, this.pagination.perPage),
        this.essService.getClaimTypes(this.cliRegId),
        this.essService.getOnGoingPayPeriod(ProfileMode.ESS)]
    )
      .pipe(
        finalize(() => this.loading = false),
        tap(([claimsResponse, claimTypes, onGoingPayPeriod]) => {
          this.claims = this.withExtraMetadatas(claimsResponse.data, claimTypes);
          this.claimTypes = claimTypes.map(item => {
            if (item.attributes.maxAllowedDaysAfterOngoingPayPeriodStart) {
              const deadline = moment(onGoingPayPeriod.beginDate)
                .add(item.attributes.maxAllowedDaysAfterOngoingPayPeriodStart, 'days');

              const keyTranslate: string = (moment().isAfter(deadline)) ?
                'CLAIMS.msg_deadline_passed' : 'CLAIMS.msg_deadline_not_passed_yet';

              const deadlineMsg = this.translate.instant(keyTranslate, {date: deadline.add(-1, 'days').format(DATE_FORMAT)});
              item.attributes.transId += ` (${deadlineMsg})`;
            }
            return item;
          });
          this.onGoingPayPeriod = onGoingPayPeriod;

          this.claimTypeTree = createClaimTypeTree(this.claimTypes);

          this.pagination.update(claimsResponse.total);
          this.sortByCreationDateDesc();
        }));
  }

  private sortByCreationDateDesc(): void {
    this.claims.sort((claim1, claim2) => {
      return -moment(claim1.creationDate).diff(moment(claim2.creationDate));
    });
  }

  private withExtraMetadatas(claims: ClaimData[], claimTypes: ClaimType[]): ClaimData[] {
    claims.forEach(claim => {
      const claimType = claimTypes.find(type => type.attributes.name === claim.name);
      claim.currencyString = claimType ? claimType.attributes.currencyString : null;
    });
    return claims;
  }

  private createNewClaim(payload) {
    const createClaim$ = this.essService.createClaimWorkflowV2(this.cliRegId, payload);

    createClaim$.pipe(
      tap(() => {
        this.claimForm.reset();
        this.modalService.modalClosed.emit('create-request');
        this.isModalOpen = false;
        this.submitting = false;
      }),
      switchMap(() => this.getClaimsData())
    )
      .subscribe();
  }

  private getAdditionalZonesData() {
    return this.claimFormComponent?.variableZones.getModifiedCardsData();
  }
}
